// src/hooks/useLogin.js
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "../services/authService";
import { jwtDecode } from "jwt-decode";
import { getAllRoles } from "../services/roleService";
import { getPermissionsByRoleId } from "../services/permissionService";

const useLogin = (setIsLoggedIn) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const sanitizeEmail = (email) => email.trim().toLowerCase();

  const safeDecode = (token) => {
    try {
      return jwtDecode(token);
    } catch (err) {
      console.warn("jwt decode failed:", err);
      return null;
    }
  };

  /// Transform array [{module, action}, ...] => { ModuleName: {view: true,..., semua: bool}, ... }
  const transformPermissionsArray = (arr = []) => {
    const actions = ["view", "add", "edit", "delete", "print"];
    const result = {};
    if (!Array.isArray(arr)) return result;

    arr.forEach((item) => {
      const module = item?.module;
      const action = (item?.action || "").toString().trim();
      if (!module || !action) return;

      if (!result[module]) {
        result[module] = {};
        actions.forEach((a) => {
          result[module][a] = false;
        });
      }

      // if action is known, set it; otherwise set dynamic key true
      if (actions.includes(action)) {
        result[module][action] = true;
      } else {
        result[module][action] = true;
      }
    });

    // compute "semua" flag
    Object.keys(result).forEach((m) => {
      const r = result[m];
      r.semua = actions.every((a) => !!r[a]);
    });

    return result;
  };

  const parsePermissionsResponse = (permsResp) => {
    if (!permsResp) return null;

    // 1) if backend returned array directly
    if (Array.isArray(permsResp)) {
      return transformPermissionsArray(permsResp);
    }

    // 2) unwrap common wrappers like { data: { permissions: [...] } } or { data: { data: { permissions: [...] } } }
    const tryPaths = [
      () => permsResp?.data?.data?.permissions,
      () => permsResp?.data?.permissions,
      () => permsResp?.permissions,
      () => permsResp?.data, // sometimes perms are directly under data
    ];

    for (const getter of tryPaths) {
      const val = getter();
      if (!val) continue;
      if (Array.isArray(val)) return transformPermissionsArray(val);
      if (typeof val === "object") {
        // if it's already normalized object: ensure keys exist for actions
        const actions = ["view", "add", "edit", "delete", "print"];
        const normalized = {};
        Object.keys(val).forEach((mod) => {
          const src = val[mod] || {};
          normalized[mod] = {};
          actions.forEach((a) => {
            normalized[mod][a] = !!src[a];
          });
          normalized[mod].semua = actions.every((a) => !!normalized[mod][a]);
        });
        return normalized;
      }
    }

    // 3) if permsResp itself looks like object module -> actions
    if (typeof permsResp === "object") {
      const firstKey = Object.keys(permsResp)[0];
      const maybeModule = firstKey && permsResp[firstKey];
      if (firstKey && maybeModule && typeof maybeModule === "object") {
        const actions = ["view", "add", "edit", "delete", "print"];
        const normalized = {};
        Object.keys(permsResp).forEach((mod) => {
          const src = permsResp[mod] || {};
          normalized[mod] = {};
          actions.forEach((a) => {
            normalized[mod][a] = !!src[a];
          });
          normalized[mod].semua = actions.every((a) => !!normalized[mod][a]);
        });
        return normalized;
      }
    }

    return null;
  };

  // --- tolerant matching helpers ---
  const normalize = (s) =>
    (s || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "") // remove spaces
      .replace(/[_-]+/g, ""); // remove underscores/dashes

  const findRoleIdByName = (rolesArray, roleNameFromToken) => {
    if (!Array.isArray(rolesArray) || !roleNameFromToken) return null;
    const tokenNorm = normalize(roleNameFromToken);

    // 1) exact case-insensitive match
    let found = rolesArray.find(
      (r) => r.name && r.name.toLowerCase() === roleNameFromToken.toLowerCase()
    );
    if (found) return found.id;

    // 2) normalized match (remove spaces/underscores/dashes)
    found = rolesArray.find((r) => normalize(r.name) === tokenNorm);
    if (found) return found.id;

    // 3) partial includes match (either side)
    found = rolesArray.find(
      (r) =>
        normalize(r.name).includes(tokenNorm) ||
        tokenNorm.includes(normalize(r.name))
    );
    if (found) return found.id;

    return null;
  };

  const handleLogin = async ({ email, password }) => {
    setError("");
    setIsSubmitting(true);

    try {
      const emailFormatValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!emailFormatValid) {
        setError("Format email tidak valid.");
        setIsSubmitting(false);
        return;
      }

      const sanitizedEmail = sanitizeEmail(email);
      const response = await login({ email: sanitizedEmail, password });

      const token = response?.data?.token;
      if (!token) throw new Error("Token tidak ditemukan di response.");

      // simpan token supaya axios interceptor pakai untuk request berikutnya
      localStorage.setItem("token", token);

      const decoded = safeDecode(token);
      const now = Math.floor(Date.now() / 1000);

      if (!decoded || decoded.exp < now || !decoded.fullName || !decoded.role) {
        localStorage.removeItem("token");
        throw new Error("Token tidak valid atau sudah kadaluarsa.");
      }

      const userInfo = {
        id: decoded.id,
        name: decoded.fullName,
        role: decoded.role,
      };

      localStorage.setItem("userInfo", JSON.stringify(userInfo));

      // --- cari roleId dengan tolerant matching ---
      let roleId = decoded?.roleId || null;
      try {
        if (!roleId) {
          console.log("[useLogin] mencoba getAllRoles() untuk cari roleId...");
          const roles = await getAllRoles();
          console.log("[useLogin] roles from API:", roles);
          roleId = findRoleIdByName(roles, decoded.role);
        }
      } catch (err) {
        console.warn(
          "[useLogin] getAllRoles() gagal:",
          err?.response?.status || err.message
        );
        roleId = null;
      }

      // --- ambil permissions via roleId jika tersedia ---
      let permissions = null;
      if (roleId) {
        try {
          console.log("[useLogin] getPermissionsByRoleId:", roleId);
          const permsResp = await getPermissionsByRoleId(roleId);
          permissions = parsePermissionsResponse(permsResp);
        } catch (err) {
          console.warn(
            "[useLogin] getPermissionsByRoleId failed:",
            err?.response?.status || err.message
          );
          permissions = null;
        }
      }

      // --- fallback: cek apakah login response membawa permissions langsung (array atau object) ---
      if (!permissions) {
        const fromLogin = parsePermissionsResponse(
          response?.data?.permissions || response?.data
        );
        if (fromLogin) {
          console.log("[useLogin] permissions found in login response");
          permissions = fromLogin;
        }
      }

      // --- ultimate fallback: kosongkan permissions (tetap login) ---
      if (!permissions) {
        console.warn(
          "[useLogin] permissions not found; using empty object (user might have limited access)"
        );
        permissions = {};
      }

      // simpan permissions (meskipun kosong)
      localStorage.setItem("permissions", JSON.stringify(permissions));
      if (roleId) localStorage.setItem("roleId", roleId);

      localStorage.setItem(
        "auth",
        JSON.stringify({ access: permissions, role: userInfo.role })
      );
      localStorage.setItem("isLoggedIn", "true");
      setIsLoggedIn(true);

      // debug quick logs (hapus nanti)
      // console.log(
      //   "[useLogin] login success. role:",
      //   userInfo.role,
      //   "roleId:",
      //   roleId
      // );
      //console.log("[useLogin] permissionsObject:", permissions);

      navigate("/dashboard");
    } catch (err) {
      console.error("[useLogin] Login error:", err);
      const message =
        err?.response?.data?.message ||
        err.message ||
        "Terjadi kesalahan saat login.";
      setError(message);

      // hanya hapus data auth
      localStorage.removeItem("token");
      localStorage.removeItem("permissions");
      localStorage.removeItem("userInfo");
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("auth");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    handleLogin,
    isSubmitting,
    error,
  };
};

export default useLogin;
